// Dev-command delivery engine. Drains the durable per-user inbox on boot
// (apply → ack-delete), then listens live: dev_inbox INSERTs (own rows —
// WALRUS applies RLS) apply+ack, dev_feed INSERTs (global) apply only —
// missing a feed event while offline is BY DESIGN ("online at that moment").
// Every payload is re-validated (applyGive / text checks) before it touches
// storage; malformed inbox rows are acked away so they can't wedge the queue.
// Errors are swallowed after console.warn — delivery must never break the game.
import type { RealtimePostgresInsertPayload, SupabaseClient } from '@supabase/supabase-js';
import {
  applyGive,
  applyOp,
  applyRemove,
  applySkip,
  iconGiftArt,
  itemGiftArt,
  potionGiftArt,
} from './applyGive';
import { skipCurrentLevel } from './levelSkip';
import { applyCodeRewards } from './codes';
import type { Achievement } from '../config/achievements';
import type { StorageLike } from './saveSync';
import { saveStorage } from '../render/saveStorage';
import { enqueueBanner } from '../ui/messageBanner';
import { enqueueDevPopup, type DevPopupKind } from '../ui/devPopup';
import { enqueueAchievementToast } from '../ui/achievementToast';
import { showBanPopup } from './banUi';
import { showCodeRewardBanner } from '../ui/codeRewardBanner';

export interface InboxRow {
  id: string;
  sender_username: string;
  sender_role?: string | null;
  kind: string;
  payload: unknown;
}

export interface InboxHooks {
  onMessage: (sender: string, text: string, senderRole?: string) => void;
  onGift: (what: string, art?: string) => void;
  onOp: () => void;
  /** /remove landed and actually took something (never fired for 'nothing'). */
  onRemove: () => void;
  /** /skip landed: the waits it cleared, and whether a live level was won. */
  onSkip: (readied: string[], levelWon: boolean) => void;
  onBan: () => void;
  /** A redeemed code's rewards landed: the labels of what was actually new. */
  onCode: (labels: string[]) => void;
  /** /give achieve landed and was NEWLY earned (never fired for repeats). */
  onAchieve: (a: Achievement) => void;
  /** /give rank landed and the rank was set. */
  onRankSet: (rank: number) => void;
  /** /give achieve all landed (count = newly earned; fires even at 0). */
  onAchieveAll: (newlyEarned: number) => void;
  /** /give achieve reset landed and the list was cleared. */
  onAchieveReset: () => void;
}

export class DevInbox {
  private userId: string | null = null;
  /**
   * Code rows already applied. A code's row is drained on demand right after
   * redeem_code returns, and Realtime may deliver the same INSERT moments
   * later — before the ack lands — so a code row is applied at most once.
   */
  private readonly seen = new Set<string>();
  /** Inbox rows applied by a drain or a live INSERT (each applies once). */
  private readonly drained = new Set<string>();

  constructor(
    private readonly client: SupabaseClient,
    private readonly storage: StorageLike,
    private readonly hooks: InboxHooks,
  ) {}

  async init(): Promise<void> {
    // Whole body (getSession included) is one try/catch — main.ts calls this
    // fire-and-forget (`void initDevInbox(...)`), so any failure here must be
    // a console.warn no-op, never an unhandled rejection.
    try {
      const { data } = await this.client.auth.getSession();
      this.userId = data.session?.user.id ?? null;
      if (!this.userId) return;
      try {
        await this.drain();
      } catch (e) {
        console.warn('dev inbox: drain failed', e);
      }
      this.subscribe();
    } catch (e) {
      console.warn('dev inbox: init failed', e);
    }
  }

  /** Apply one row's effect (validated; malformed rows are a warn + no-op). */
  handleRow(row: InboxRow): void {
    if (row.kind === 'code') {
      if (this.seen.has(row.id)) return;
      this.seen.add(row.id);
      const labels = applyCodeRewards(
        this.storage,
        (row.payload as { rewards?: unknown } | null)?.rewards,
      );
      if (labels) this.hooks.onCode(labels);
      else console.warn('dev inbox: dropped malformed code', row.id);
      return;
    }
    if (row.kind === 'give') {
      const applied = applyGive(this.storage, row.payload, {
        onAchieve: (a) => this.hooks.onAchieve(a),
        onRankSet: (rank) => this.hooks.onRankSet(rank),
        onAchieveAll: (n) => this.hooks.onAchieveAll(n),
        onAchieveReset: () => this.hooks.onAchieveReset(),
      });
      if (applied) {
        const what = (row.payload as { what?: unknown }).what;
        // Popups only for the spec'd kinds; tower/level keep their existing UX
        // and rank/achieve announce through their own toasts above.
        if (what === 'coins' || what === 'bags' || what === 'unlock') this.hooks.onGift(what);
        // A hub level give announces itself like a feature unlock.
        else if (what === 'hublevel') this.hooks.onGift('unlock');
        else if (what === 'debris') this.hooks.onGift('debris');
        else if (what === 'capycoins') this.hooks.onGift('capycoins');
        else if (what === 'darkcoins') this.hooks.onGift('darkcoins');
        else if (what === 'lightcoins') this.hooks.onGift('lightcoins');
        else if (what === 'capsules') this.hooks.onGift('capsules');
        else if (what === 'goldenbags') this.hooks.onGift('goldenbags');
        else if (what === 'icon') {
          const art = iconGiftArt((row.payload as { value?: unknown }).value);
          this.hooks.onGift('icon', art ?? undefined);
        } else if (what === 'item') {
          const art = itemGiftArt((row.payload as { value?: unknown }).value);
          this.hooks.onGift('item', art ?? undefined);
        } else if (what === 'potion') {
          const art = potionGiftArt((row.payload as { value?: unknown }).value);
          this.hooks.onGift('potion', art ?? undefined);
        }
      } else {
        console.warn('dev inbox: dropped malformed give', row.id);
      }
      return;
    }
    if (row.kind === 'message') {
      const text = (row.payload as { text?: unknown } | null)?.text;
      if (typeof text === 'string' && text.length > 0 && text.length <= 200) {
        this.hooks.onMessage(row.sender_username, text, row.sender_role ?? undefined);
      } else {
        console.warn('dev inbox: dropped malformed message', row.id);
      }
      return;
    }
    if (row.kind === 'op') {
      applyOp(this.storage);
      this.hooks.onOp();
      return;
    }
    if (row.kind === 'skip') {
      // Storage first, so a bag readied by the skip is already ready when the
      // Level Complete screen (and anything it leads to) reads it.
      const readied = applySkip(this.storage, Date.now());
      const levelWon = skipCurrentLevel();
      this.hooks.onSkip(readied, levelWon);
      return;
    }
    if (row.kind === 'remove') {
      // /give in reverse (spec 2026-10-06). Naming something the player does
      // not have does nothing at all — no write and no popup.
      const r = applyRemove(this.storage, row.payload);
      if (r === 'removed') this.hooks.onRemove();
      else if (r === 'invalid') console.warn('dev inbox: dropped malformed remove', row.id);
      return;
    }
    if (row.kind === 'ban') {
      this.hooks.onBan();
      return;
    }
    console.warn('dev inbox: dropped unknown kind', row.id);
  }

  /** Apply anything waiting now (the CODES panel calls this after a redeem). */
  async drainNow(): Promise<void> {
    // Booted signed out and signed in since: start properly (init drains).
    if (!this.userId) return this.init();
    await this.drain();
  }

  private async drain(): Promise<void> {
    const { data, error } = await this.client
      .from('dev_inbox')
      .select('id, sender_username, sender_role, kind, payload')
      .order('created_at', { ascending: true });
    if (error) throw new Error(error.message);
    for (const row of (data ?? []) as InboxRow[]) {
      // Once the live channel is up an on-demand drain can see a row Realtime
      // also delivers; whichever comes second skips it.
      if (this.drained.has(row.id)) continue;
      this.drained.add(row.id);
      this.handleRow(row);
      await this.ack(row.id);
    }
  }

  private async ack(id: string): Promise<void> {
    const { error } = await this.client.from('dev_inbox').delete().eq('id', id);
    if (error)
      console.warn('dev inbox: ack failed (row may re-apply next boot)', id, error.message);
  }

  private subscribe(): void {
    this.client
      .channel('dev-deliveries')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'dev_inbox',
          filter: `recipient=eq.${this.userId}`,
        },
        (msg: RealtimePostgresInsertPayload<InboxRow>) => {
          if (this.drained.has(msg.new.id)) return;
          this.drained.add(msg.new.id);
          this.handleRow(msg.new);
          void this.ack(msg.new.id);
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'dev_feed' },
        (msg: RealtimePostgresInsertPayload<InboxRow>) => this.handleRow(msg.new),
      )
      .subscribe();
  }
}

// ── module singleton (wired in main.ts; inert when unconfigured) ────────────
let inbox: DevInbox | null = null;

export async function initDevInbox(client: SupabaseClient): Promise<void> {
  if (inbox) return;
  inbox = new DevInbox(client, saveStorage(), liveHooks(client));
  await inbox.init();
}

/**
 * Drain the inbox now — a redeemed code's row is waiting and Realtime need not
 * be relied on. A player who signed in after boot has no inbox yet, so this
 * starts it (init drains).
 */
export async function drainDevInbox(client: SupabaseClient): Promise<void> {
  try {
    if (!inbox) {
      await initDevInbox(client);
      return;
    }
    await inbox.drainNow();
  } catch (e) {
    console.warn('dev inbox: drain on demand failed', e);
  }
}

/**
 * E2E only (main.ts exposes it under VITE_E2E): run one delivery row through
 * the real handler and the real popups, with no server — so a spec can drive
 * /skip or a /give exactly as a Realtime INSERT would, in a build without
 * Supabase. A `ban` row is ignored here (it needs a client to sign out).
 */
export function deliverForTest(row: InboxRow): void {
  if (row.kind === 'ban') return;
  new DevInbox({} as SupabaseClient, saveStorage(), liveHooks(null)).handleRow(row);
}

/** The hooks a live delivery fires: banners, popups and toasts. */
function liveHooks(client: SupabaseClient | null): InboxHooks {
  return {
    onMessage: enqueueBanner,
    onGift: (what, art) => enqueueDevPopup(what as DevPopupKind, art ? { art } : undefined),
    onOp: () => enqueueDevPopup('op'),
    onRemove: () => enqueueDevPopup('remove'),
    onSkip: () => enqueueDevPopup('skip'),
    onBan: () => {
      if (client) showBanPopup(client);
    },
    onCode: showCodeRewardBanner,
    onAchieve: (a) =>
      enqueueAchievementToast({ title: 'You have been awarded', body: a.name, iconKey: a.icon }),
    onRankSet: () =>
      enqueueAchievementToast({
        title: 'Your rank has been updated',
        body: '',
        iconKey: 'medal-gold',
        overlay: '?',
      }),
    onAchieveAll: () =>
      enqueueAchievementToast({
        title: 'You have been awarded',
        body: 'All achievements',
        iconKey: 'medal-gold',
      }),
    onAchieveReset: () =>
      enqueueAchievementToast({
        title: 'Your achievements have been reset',
        body: '',
        iconKey: 'medal-bronze',
        overlay: '✕',
      }),
  };
}
